import React, { useState, useEffect } from 'react';
import { apiService } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Bot, Mail, Shield, Save, Eye, EyeOff, CheckCircle2, AlertCircle, Loader2, Send } from 'lucide-react';

const SettingsPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingSMTP, setTestingSMTP] = useState(false);
  const [settings, setSettings] = useState(null);

  const [aiForm, setAiForm] = useState({ ai_api_key: '', ai_model: 'gpt-4o' });
  const [smtpForm, setSmtpForm] = useState({
    smtp_host: '', smtp_port: 587, smtp_email: '', smtp_password: '',
    smtp_encryption: 'tls', smtp_from_name: ''
  });

  const [showApiKey, setShowApiKey] = useState(false);
  const [showSmtpPassword, setShowSmtpPassword] = useState(false);

  const aiModels = [
    { value: 'gpt-4o', label: 'GPT-4o (Recommended)' },
    { value: 'gpt-4o-mini', label: 'GPT-4o Mini (Faster)' },
    { value: 'gpt-4', label: 'GPT-4' }
  ];

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const response = await apiService.getSettings();
      setSettings(response.data);
      setAiForm({ ai_api_key: '', ai_model: response.data.ai_model || 'gpt-4o' });
      setSmtpForm({
        smtp_host: response.data.smtp_host || '',
        smtp_port: response.data.smtp_port || 587,
        smtp_email: response.data.smtp_email || '',
        smtp_password: '',
        smtp_encryption: response.data.smtp_encryption || 'tls',
        smtp_from_name: response.data.smtp_from_name || ''
      });
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const saveAISettings = async () => {
    setSaving(true);
    try {
      const updateData = { ai_model: aiForm.ai_model };
      if (aiForm.ai_api_key) updateData.ai_api_key = aiForm.ai_api_key;
      await apiService.updateSettings(updateData);
      toast.success('AI settings saved');
      loadSettings();
      setAiForm(prev => ({ ...prev, ai_api_key: '' }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const saveSMTPSettings = async () => {
    if (!smtpForm.smtp_host || !smtpForm.smtp_email) {
      toast.error('SMTP Host and Email are required');
      return;
    }
    setSaving(true);
    try {
      const updateData = {
        smtp_host: smtpForm.smtp_host,
        smtp_port: smtpForm.smtp_port,
        smtp_email: smtpForm.smtp_email,
        smtp_encryption: smtpForm.smtp_encryption,
        smtp_from_name: smtpForm.smtp_from_name
      };
      if (smtpForm.smtp_password) updateData.smtp_password = smtpForm.smtp_password;
      await apiService.updateSettings(updateData);
      toast.success('SMTP settings saved');
      loadSettings();
      setSmtpForm(prev => ({ ...prev, smtp_password: '' }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const testSMTPConnection = async () => {
    setTestingSMTP(true);
    try {
      const result = await apiService.testSMTP();
      if (result.data.status === 'success') {
        toast.success('SMTP connection successful!');
      } else {
        toast.error(result.data.message);
      }
    } catch (error) {
      toast.error('SMTP test failed');
    } finally {
      setTestingSMTP(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="page-container" data-testid="settings-page">
      <div className="mb-6">
        <h1 className="text-2xl font-heading font-bold text-slate-900">Settings</h1>
        <p className="text-sm text-slate-500 mt-1">Configure AI and email alert settings</p>
      </div>

      <Tabs defaultValue="ai" className="space-y-6">
        <TabsList>
          <TabsTrigger value="ai" className="gap-2" data-testid="ai-settings-tab">
            <Bot className="h-4 w-4" />AI Settings
          </TabsTrigger>
          <TabsTrigger value="email" className="gap-2" data-testid="email-settings-tab">
            <Mail className="h-4 w-4" />Email / SMTP
          </TabsTrigger>
        </TabsList>

        {/* AI Settings */}
        <TabsContent value="ai">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-heading flex items-center gap-2">
                <Bot className="h-5 w-5 text-teal-600" />AI Configuration
              </CardTitle>
              <CardDescription>Configure the AI model for SQL and XML generation</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>OpenAI API Key</Label>
                    <div className="relative">
                      <Input
                        type={showApiKey ? 'text' : 'password'}
                        value={aiForm.ai_api_key}
                        onChange={(e) => setAiForm({ ...aiForm, ai_api_key: e.target.value })}
                        placeholder={settings?.ai_api_key_set ? '••••••••••••••••' : 'sk-...'}
                        data-testid="ai-api-key-input"
                      />
                      <button type="button" onClick={() => setShowApiKey(!showApiKey)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                        {showApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    <p className="text-xs text-slate-500">
                      {settings?.ai_api_key_set ? (
                        <span className="flex items-center gap-1 text-emerald-600"><CheckCircle2 className="h-3 w-3" />API key configured</span>
                      ) : (
                        <span className="flex items-center gap-1 text-amber-600"><AlertCircle className="h-3 w-3" />No API key configured</span>
                      )}
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label>AI Model</Label>
                    <Select value={aiForm.ai_model} onValueChange={(v) => setAiForm({ ...aiForm, ai_model: v })}>
                      <SelectTrigger data-testid="ai-model-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {aiModels.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="bg-slate-50 rounded-lg p-4">
                  <h4 className="font-medium text-slate-900 mb-3">AI Features</h4>
                  <ul className="space-y-2 text-sm text-slate-600">
                    <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-teal-600 mt-0.5" />SQL query generation from natural language</li>
                    <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-teal-600 mt-0.5" />Tally XML voucher generation</li>
                    <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-teal-600 mt-0.5" />Tally fetch XML request generation</li>
                    <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-teal-600 mt-0.5" />Query safety validation</li>
                  </ul>
                </div>
              </div>
              <div className="flex justify-end pt-4 border-t border-slate-200">
                <Button className="bg-teal-700 hover:bg-teal-600" onClick={saveAISettings} disabled={saving} data-testid="save-ai-settings-btn">
                  {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving...</> : <><Save className="h-4 w-4 mr-2" />Save AI Settings</>}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* SMTP Settings */}
        <TabsContent value="email">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-heading flex items-center gap-2">
                <Mail className="h-5 w-5 text-teal-600" />SMTP Configuration
              </CardTitle>
              <CardDescription>Configure SMTP for failure alert emails</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="grid grid-cols-3 gap-3">
                    <div className="col-span-2 space-y-2">
                      <Label>SMTP Host *</Label>
                      <Input value={smtpForm.smtp_host} onChange={(e) => setSmtpForm({ ...smtpForm, smtp_host: e.target.value })} placeholder="smtp.gmail.com" data-testid="smtp-host-input" />
                    </div>
                    <div className="space-y-2">
                      <Label>Port</Label>
                      <Input type="number" value={smtpForm.smtp_port} onChange={(e) => setSmtpForm({ ...smtpForm, smtp_port: parseInt(e.target.value) || 587 })} />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Email Address *</Label>
                    <Input type="email" value={smtpForm.smtp_email} onChange={(e) => setSmtpForm({ ...smtpForm, smtp_email: e.target.value })} placeholder="alerts@yourcompany.com" data-testid="smtp-email-input" />
                  </div>

                  <div className="space-y-2">
                    <Label>Password</Label>
                    <div className="relative">
                      <Input
                        type={showSmtpPassword ? 'text' : 'password'}
                        value={smtpForm.smtp_password}
                        onChange={(e) => setSmtpForm({ ...smtpForm, smtp_password: e.target.value })}
                        placeholder={settings?.smtp_configured ? '••••••••' : 'Enter password'}
                        data-testid="smtp-password-input"
                      />
                      <button type="button" onClick={() => setShowSmtpPassword(!showSmtpPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                        {showSmtpPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>Encryption</Label>
                      <Select value={smtpForm.smtp_encryption} onValueChange={(v) => setSmtpForm({ ...smtpForm, smtp_encryption: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="tls">TLS (Recommended)</SelectItem>
                          <SelectItem value="ssl">SSL</SelectItem>
                          <SelectItem value="none">None</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>From Name</Label>
                      <Input value={smtpForm.smtp_from_name} onChange={(e) => setSmtpForm({ ...smtpForm, smtp_from_name: e.target.value })} placeholder="D2Tally Alerts" />
                    </div>
                  </div>

                  {settings?.smtp_configured && (
                    <Button variant="outline" onClick={testSMTPConnection} disabled={testingSMTP} data-testid="test-smtp-btn">
                      {testingSMTP ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Testing...</> : <><Send className="h-4 w-4 mr-2" />Test Connection</>}
                    </Button>
                  )}
                </div>

                <div className="space-y-4">
                  <div className="bg-slate-50 rounded-lg p-4">
                    <h4 className="font-medium text-slate-900 mb-3">Alert Triggers</h4>
                    <ul className="space-y-2 text-sm text-slate-600">
                      <li className="flex items-start gap-2"><Mail className="h-4 w-4 text-red-500 mt-0.5" />Tally posting failures</li>
                      <li className="flex items-start gap-2"><Mail className="h-4 w-4 text-amber-500 mt-0.5" />Connection timeouts</li>
                      <li className="flex items-start gap-2"><Mail className="h-4 w-4 text-red-500 mt-0.5" />Fetch job errors</li>
                      <li className="flex items-start gap-2"><Mail className="h-4 w-4 text-amber-500 mt-0.5" />Partial sync failures</li>
                    </ul>
                  </div>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-medium text-blue-900 mb-2 flex items-center gap-2"><Shield className="h-4 w-4" />Security Note</h4>
                    <p className="text-sm text-blue-800">Passwords are encrypted before storage. For Gmail, use App Passwords instead of your regular password.</p>
                  </div>
                </div>
              </div>
              <div className="flex justify-end pt-4 border-t border-slate-200">
                <Button className="bg-teal-700 hover:bg-teal-600" onClick={saveSMTPSettings} disabled={saving} data-testid="save-smtp-settings-btn">
                  {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving...</> : <><Save className="h-4 w-4 mr-2" />Save SMTP Settings</>}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SettingsPage;
